import {
    Container,
    Typography,
    Grid,
    Box,
    Button,
    CircularProgress,
    IconButton,
} from '@mui/material';
import CourseCard from './CourseCard';
import { Download, Email, Launch, Print } from '@mui/icons-material';

import { useCourses } from '../../context/CourseContext';
import ErrorPage from '../blogs/ErrorPage';
import LMSPageSkeleton from '../../components/skeleton/lms/LMSPageSkeleton';
import { useEffect, useState } from 'react';
import { logger } from '../../utils/SiteHelpers';
import { userAxios } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/reusable/SEO';
import Loader from '../../components/reusable/Loader/Loader';
import PreviewCertificateModal from './PreviewCertificateModal';

const LMSPage = () => {
    const {
        courses,
        loading,
        emailCertificate,
        downloadCertificate,
        fetchCourses,
    } = useCourses();
    const { accessToken } = useAuth();
    const [certificate, setCertificate] = useState([]);
    const [loadingMap, setLoadingMap] = useState({});
    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [courseId, setCourseId] = useState(null);

    const handleLaunchPdf = async (courseId) => {
        // if (!allLessonsCompleted) return;

        try {
            const result = await downloadCertificate(courseId, true);
            if (result.success) {
                setPdfUrl(result.pdfUrl);
                setPdfModalOpen(true);
                setCourseId(courseId);
            }
        } catch (error) {
            console.error('Failed to load certificate:', error);
        }
    };
    const handleSendEmail = async (course_id) => {
        setLoadingMap((prev) => ({ ...prev, [course_id + '_email']: true }));
        try {
            await emailCertificate(course_id);
        } finally {
            setLoadingMap((prev) => ({
                ...prev,
                [course_id + '_email']: false,
            }));
        }
    };

    const handleDownloadCertificate = async (course_id) => {
        setLoadingMap((prev) => ({ ...prev, [course_id + '_download']: true }));
        try {
            await downloadCertificate(course_id);
        } finally {
            setLoadingMap((prev) => ({
                ...prev,
                [course_id + '_download']: false,
            }));
        }
    };

    const getCertificate = async () => {
        try {
            if (accessToken) {
                const res = await userAxios.get(`cle/certificates`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                setCertificate(res.data.data);
            }
        } catch (err) {
            logger.log(err.message || 'Failed to load courses');
        }
    };

    useEffect(() => {
        fetchCourses();
        getCertificate();
    }, []);

    if (loading) {
        return <LMSPageSkeleton />;
    }

    if (!courses || courses.length === 0) {
        return (
            <ErrorPage
                title="Courses Not Found"
                buttonLink={`/webinars`}
                buttonText={'Back to webinars'}
            />
        );
    }

    return (
        <>
            <SEO pageTitle={`Available Webinars | Toolkit.law`} />
            {courses.length > 0 && (
                <Container sx={{ py: 4 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        mb={3}
                        gap={2}
                    >
                        <Typography variant="h5" fontWeight="bold">
                            Available Webinars
                        </Typography>
                        {/* <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search courses..."
                            sx={{ width: '20rem' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        /> */}
                    </Box>

                    <Grid container spacing={3}>
                        {/* Course Cards Column */}
                        <Grid item xs={12} md={certificate.length > 0 ? 9 : 12}>
                            <Grid container spacing={3}>
                                {courses.map((course) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={certificate.length > 0 ? 6 : 4}
                                        md={certificate.length > 0 ? 6 : 4}
                                        key={course.id}
                                    >
                                        <CourseCard course={course} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        {/* Certificate Column */}
                        {certificate.length > 0 && (
                            <Grid item xs={12} md={3}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        py: 2,
                                        px: 2,
                                        backgroundColor:
                                            'rgba(246, 159, 54, 0.5)',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold">
                                        Your Certificates
                                    </Typography>
                                    {certificate.map((item) => {
                                        const emailLoading =
                                            loadingMap[
                                                item.course_id + '_email'
                                            ];
                                        const downloadLoading =
                                            loadingMap[
                                                item.course_id + '_download'
                                            ];

                                        return (
                                            <Box
                                                key={item.course_id}
                                                display="block"
                                                alignItems="center"
                                                mt={1}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ flex: 1 }}
                                                >
                                                    {item.course_name}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleDownloadCertificate(
                                                                item.course_id,
                                                            )
                                                        }
                                                        sx={{
                                                            backgroundColor:
                                                                'transparent', // or another base color
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(255, 255, 255, 0.2)',
                                                            },
                                                        }}
                                                        title="Download Certificate"
                                                    >
                                                        <Download fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleSendEmail(
                                                                item.course_id,
                                                            )
                                                        }
                                                        sx={{
                                                            backgroundColor:
                                                                'transparent', // or another base color
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(255, 255, 255, 0.2)',
                                                            },
                                                        }}
                                                        title="Email Certificate"
                                                    >
                                                        <Email fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            backgroundColor:
                                                                'transparent', // or another base color
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(255, 255, 255, 0.2)',
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            downloadCertificate(
                                                                item.course_id,
                                                                false,
                                                                true,
                                                            )
                                                        }
                                                        title="Print Certificate"
                                                    >
                                                        <Print fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            backgroundColor:
                                                                'transparent', // or another base color
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(255, 255, 255, 0.2)',
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            handleLaunchPdf(
                                                                item.course_id,
                                                            )
                                                        }
                                                        title="Preview Certificate"
                                                    >
                                                        <Launch fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                    {pdfModalOpen && (
                        <PreviewCertificateModal
                            open={pdfModalOpen}
                            setOpen={setPdfModalOpen}
                            pdfUrl={pdfUrl}
                            courseId={courseId}
                        />
                    )}
                </Container>
            )}
            <Loader />
        </>
    );
};

export default LMSPage;
